// Where the login surface answers.
//
// Its own module, where identity/routes.ts keeps ADMIN_ENDPOINTS beside the
// routes it names, and the difference is not taste. rateLimit.ts has to name
// these urls to limit them, and routes.ts has to read `clientIp` out of
// rateLimit.ts to record where a request came from. Those two imports in one
// pair of files is a cycle, and the way it fails is specific and nasty:
// whichever of the two is evaluated first decides whether
// `DEFAULT_RATE_LIMITS` reads these constants before they exist. Splitting the
// names into a module that imports nothing removes the question rather than
// leaving it to import order.
//
// Signing out is its own url rather than a DELETE on the one above it, because
// the rate limiter keys its rules on the url and knows nothing about methods: a
// rule sized for guessing at a six-digit code would otherwise also be the rule
// sized for somebody closing a tab.

// The two enrolment urls are named for the factor rather than for TOTP, and that
// is the seam. WebAuthn, when it comes, is another kind of row in the same table
// reached through the same pair of urls with a different body — not a second
// login surface with its own session issuer, which is the shape that ends with
// two places deciding who is signed in and only one of them being audited.

/**
 * Where a rotated session token comes back.
 *
 * HERE RATHER THAN IN auth/rotation.ts, AND FOR THE REASON THIS MODULE EXISTS AT
 * ALL: xp/console/src/api/endpoints.ts re-exports from this file precisely
 * because it imports nothing, so a browser bundle can learn a name without
 * pulling Fastify in behind it. The header of that file celebrates being rid of
 * the last hand-kept copy of a url; a hand-kept copy of a header name would be
 * the same defect wearing a different hat, and it would fail in the worst
 * available way — silently, with the console never adopting a replacement and
 * every session quietly dying one grace window after its first request.
 *
 * A HEADER AND NOT A FIELD IN THE BODY, which is the one place this departs from
 * what cors.ts says the house does. Two reasons, and the second is the one that
 * decided it.
 *
 *   Every response of a rotating session has to carry it — the 200s, the 403 a
 *   nova_admin without a factor gets, the 429 the limiter writes, and whatever
 *   Fastify produces when a handler throws. A body field means every one of
 *   those construction sites has to remember, and the one that forgets strands a
 *   caller holding a token that is about to be called a replay.
 *
 *   The credential is already a header on the way in, and api/client.ts in the
 *   console argues why it stays out of bodies: "a body is a thing proxies log
 *   and browsers keep in memory dumps for longer than a header, and there is no
 *   reason for a credential to be in both places." A replacement token is the
 *   same credential travelling the other way.
 *
 * Lower case because that is how Fastify and `fetch` both normalise it, and
 * comparing two spellings of one header is a bug nobody enjoys.
 */
export const SESSION_TOKEN_HEADER = "x-xp-session-token";

/**
 * The name four request schemas and one browser agree on for a re-presented
 * authenticator.
 *
 * HERE RATHER THAN IN auth/freshFactor.ts, WHERE IT WAS DEFINED UNTIL
 * 2026-08-30, AND THE MOVE IS THIS MODULE'S WHOLE POINT BEING PROVED THE HARD
 * WAY. The docstring above says this file exists so "a browser bundle can learn
 * a name without pulling Fastify in behind it". `freshFactor.ts` is a server
 * module: it imports `db.js`, `log.js` and `secondFactor.js`, and that last one
 * imports `codes.js`, which opens with `node:crypto`. So the console importing
 * one nine-word string from it pulled the login-code and TOTP machinery into a
 * browser build's module graph.
 *
 * The build refused, which is the good half — `randomInt` is not exported by
 * vite's browser shim, so `npm run build:staging` in xp/console failed outright
 * rather than shipping a stub. The bad half is that it had been failing for days
 * and the suite was green throughout, because nothing in it builds a browser
 * package; verify-build.mjs reads dist/ and only runs as a firebase predeploy,
 * which is to say only when somebody is already deploying.
 *
 * Note what it would have cost had `randomInt` not been in the way. `createHmac`
 * and `timingSafeEqual` were externalised silently in the same build. A module
 * graph that reaches server auth code and *succeeds* is the shape where a pepper
 * or a comparison lands in a bundle, and no error says so.
 */
export const FRESH_FACTOR_FIELD = "second_factor_code";

export const AUTH_ENDPOINTS = {
  requestLoginCode: "/v1/auth/login-codes",
  createSession: "/v1/auth/sessions",
  endSession: "/v1/auth/sessions/current",
  beginSecondFactor: "/v1/auth/second-factor",
  confirmSecondFactor: "/v1/auth/second-factor/confirmation",
  resetSecondFactor: "/v1/auth/second-factor/reset",

  /**
   * A person's own sessions, and ending the ones that are not this one.
   *
   * BOTH ARE POSTS, INCLUDING THE ONE THAT ONLY READS. routes.ts argues that a
   * `GET /v1/auth/session` "would answer somebody who found a token", and that
   * objection is not solved by a gate a GET cannot carry: the six digits proving
   * the authenticator have to travel in a body, because FRESH_FACTOR_FIELD is a
   * body field on purpose — "a header is attached by client code once and then
   * sent with whatever requests that code happens to make, whereas a body field
   * can only be on the request it was typed for". A read that requires proof is
   * therefore a POST, and the verb is a consequence of the gate rather than an
   * accident.
   */
  listSessions: "/v1/auth/sessions/list",
  endOtherSessions: "/v1/auth/sessions/others",
} as const;
